<template>
    <div class="nc-composer">
        <new-chat-emoji-picker
            v-if="showEmojiPicker"
            @select="handleEmojiSelect"
        />

        <div v-show="sendError" class="nc-composer__error">{{ sendError }}</div>

        <div v-if="selectedFile" class="nc-composer__file">
            <svg class="nc-composer__file-icon" width="14" height="14" aria-hidden="true">
                <use href="#icon-attach"></use>
            </svg>
            <span class="nc-composer__file-name">{{ selectedFile.name }}</span>
            <button
                type="button"
                class="nc-composer__file-remove"
                :disabled="isSending"
                @click="clearFile"
            >
                <svg width="10" height="10" aria-hidden="true"><use href="#icon-close"></use></svg>
            </button>
        </div>

        <div class="nc-composer__input-shell">
            <v-button
                type="button"
                class="nc-composer__btn nc-composer__btn_emoji button_type_icon button_type_static button__no_min-width"
                :disabled="isSending"
                :class="{ 'is-active': showEmojiPicker }"
                @mousedown.native.prevent
                @click.native.stop="toggleEmojiPicker"
            >
                <span class="nc-composer__emoji-icon" aria-hidden="true">🙂</span>
            </v-button>

            <v-message-area
                ref="msgArea"
                v-model="text"
                placeholder="Введите сообщение"
                class="nc-composer__area"
                :disabled="isSending"
                @click="handleAreaSend"
            />

            <div class="nc-composer__actions">
                <v-button
                    type="button"
                    class="nc-composer__btn button_type_icon button_type_static button__no_min-width"
                    :disabled="isSending"
                    :class="{ 'is-active': quickRepliesOpen }"
                    @click="setQuickRepliesOpen(!quickRepliesOpen)"
                >
                    <svg aria-hidden="true" width="20" height="20">
                        <use href="#icon-chat-fast"></use>
                    </svg>
                </v-button>
                <v-button
                    type="button"
                    class="nc-composer__btn button_type_icon button_type_static button__no_min-width"
                    :disabled="isSending"
                    @click="triggerFilePicker"
                >
                    <svg aria-hidden="true" width="18" height="18">
                        <use href="#icon-attach"></use>
                    </svg>
                </v-button>
                <v-button
                    type="button"
                    class="nc-composer__btn nc-composer__btn_send button_type_icon button_type_static button__no_min-width"
                    :disabled="!canSend"
                    :loading="isSending"
                    @click="handleButtonSend"
                >
                    <svg v-if="!isSending" aria-hidden="true" width="18" height="18">
                        <use href="#icon-send"></use>
                    </svg>
                </v-button>
            </div>
        </div>

        <input
            ref="fileInput"
            type="file"
            style="display: none"
            :disabled="isSending"
            @change="handleFileChange"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import vMessageArea from '../../../global/messageArea.vue'
import vButton from '../../../global/vButton.vue'
import NewChatEmojiPicker from './NewChatEmojiPicker.vue'
import api from '@api'

export default {
    name: 'NewChatComposer',
    components: { vMessageArea, vButton, NewChatEmojiPicker },

    data() {
        return {
            text: '',
            selectedFile: null,
            isSending: false,
            sendError: '',
            showEmojiPicker: false,
        }
    },

    computed: {
        ...mapGetters('newChat', ['activeChat', 'quickRepliesOpen', 'quickReplyPending']),
        canSend() {
            return (this.selectedFile !== null || this.text.trim().length > 0) && !this.isSending
        },
    },

    watch: {
        text() {
            if (this.sendError) this.sendError = ''
        },
        quickReplyPending(phrase) {
            if (!phrase) return
            this.text = this.text ? this.text + '\n' + phrase.name : phrase.name
            this.$store.dispatch('newChat/clearQuickReply')
        },
        showEmojiPicker(val) {
            if (val) {
                this.$nextTick(() => {
                    document.addEventListener('click', this.closeEmojiPicker)
                    document.addEventListener('keydown', this.onEmojiPickerKeydown)
                })
            } else {
                document.removeEventListener('click', this.closeEmojiPicker)
                document.removeEventListener('keydown', this.onEmojiPickerKeydown)
            }
        },
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeEmojiPicker)
        document.removeEventListener('keydown', this.onEmojiPickerKeydown)
    },

    methods: {
        ...mapActions('newChat', ['appendMessage', 'setQuickRepliesOpen']),

        // --- Emoji ---
        getTextarea() {
            // messageArea.vue зберігає нативний <textarea> у $refs.textarea
            const area = this.$refs.msgArea
            return area && area.$refs && area.$refs.textarea
        },
        toggleEmojiPicker() {
            this.showEmojiPicker = !this.showEmojiPicker
        },
        closeEmojiPicker() {
            this.showEmojiPicker = false
        },
        onEmojiPickerKeydown(e) {
            if (e.key === 'Escape') this.showEmojiPicker = false
        },
        handleEmojiSelect(emoji) {
            const ta = this.getTextarea()

            // Якщо textarea у фокусі (@mousedown.prevent на кнопках пікера зберігає фокус),
            // selectionStart відображає поточну позицію курсора.
            // Якщо фокус із якоїсь причини вже зник — вставляємо в кінець.
            const pos = ta ? ta.selectionStart : this.text.length
            const end = ta ? ta.selectionEnd  : this.text.length

            this.text = this.text.slice(0, pos) + emoji + this.text.slice(end)

            // Позиція курсора після вставленого emoji (враховуємо UTF-16 довжину)
            const newPos = pos + emoji.length

            this.$nextTick(() => {
                if (ta) {
                    ta.focus()
                    ta.setSelectionRange(newPos, newPos)
                }
            })

            this.showEmojiPicker = false
        },

        // --- Files ---
        triggerFilePicker() {
            this.$refs.fileInput.click()
        },

        handleFileChange(event) {
            const file = event.target.files[0]
            if (file) this.selectedFile = file
            // Reset so selecting the same file again fires @change
            event.target.value = ''
        },

        clearFile() {
            this.selectedFile = null
        },

        // Fires when message-area emits @click (Ctrl+Enter).
        // The message-area already cleared the textarea DOM at this point.
        handleAreaSend(textFromArea) {
            const msg = (textFromArea || '').trim()
            this.text = '' // sync v-model with the already-cleared DOM
            if (this.isSending) return
            if (!this.selectedFile && !msg) return
            this.sendMessage(msg)
        },

        handleButtonSend() {
            if (this.isSending) return
            const msg = this.text.trim()
            if (!this.selectedFile && !msg) return
            this.text = ''
            this.sendMessage(msg)
        },

        async sendMessage(msg) {
            const chatId = this.activeChat && this.activeChat.id
            if (!chatId) return

            this.isSending = true
            this.sendError = ''

            const isMedia = this.selectedFile !== null
            const hasText = msg.length > 0

            try {
                // Шаг 1: отправляем файл, если прикреплён
                if (isMedia) {
                    await this.dispatchMessage(chatId, this.buildMediaPayload(), {
                        type: 'media',
                        message: this.selectedFile.name,
                    })
                    this.selectedFile = null
                }


                // Шаг 2: отправляем текст, если есть
                if (hasText) {
                    const textPayload = { message: msg, type: 'text' }
                    if (this.activeChat && this.activeChat.company_platform_id) {
                        textPayload.company_platform_id = this.activeChat.company_platform_id
                    }
                    await this.dispatchMessage(chatId, textPayload, {
                        type: 'text',
                        message: msg,
                    })
                }
            } catch (err) {
                const errMsg = (err && err.message) || 'Ошибка отправки. Попробуйте снова.'
                this.sendError = errMsg
                this.$toast.error(errMsg)
                // Восстанавливаем текст, чтобы пользователь мог повторить отправку.
                // Файл восстанавливать не нужно: если он не ушёл — он ещё прикреплён,
                // если ушёл — selectedFile уже null.
                if (hasText) this.text = msg
            } finally {
                this.isSending = false
            }
        },

        async dispatchMessage(chatId, payload, fallback) {
            const response = await api.newChat.createMessage(chatId, payload)
            const serverMsg = response && (response.data || response)
            if (serverMsg && serverMsg.id) {
                this.appendMessage({ ...serverMsg, send_status: 'send' })
            } else {
                this.appendMessage({
                    id: Date.now(),
                    sender_id: 'operator',
                    date_created: this.formatTime(new Date()),
                    send_status: 'send',
                    ...fallback,
                })
            }
        },

        buildMediaPayload() {
            const formData = new FormData()
            formData.append('type', 'media')
            formData.append('file', this.selectedFile)
            if (this.activeChat && this.activeChat.company_platform_id) {
                formData.append('company_platform_id', this.activeChat.company_platform_id)
            }
            return formData
        },

        formatTime(date) {
            const h = String(date.getHours()).padStart(2, '0')
            const m = String(date.getMinutes()).padStart(2, '0')
            return `${h}:${m}`
        },
    },
}
</script>

<style lang="scss">
@import '../../../../../../gulp-src/app/scss/_variables';

.nc-composer {
    flex: 0 0 auto;
    border-top: 1px solid $global-light-gray;
    padding-top: 8px;
    background: $global-white;

    // --- Error ---
    &__error {
        font-size: 12px;
        color: #e94a4a;
        padding: 0 4px 6px;
    }

    // --- Attached file chip ---
    &__file {
        display: flex;
        align-items: center;
        gap: 6px;
        background: rgba(108, 79, 200, 0.06);
        border: 1px solid rgba(108, 79, 200, 0.22);
        border-radius: 4px;
        padding: 5px 10px;
        margin-bottom: 6px;
        min-width: 0;
    }

    &__file-icon {
        flex: 0 0 auto;
        color: $global-dark-3a;
    }

    &__file-name {
        flex: 1 1 auto;
        font-size: 13px;
        font-weight: 500;
        color: $global-black;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__file-remove {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        color: $global-gray;
        transition: background 0.15s ease, color 0.15s ease;

        &:hover {
            background: rgba(0, 0, 0, 0.08);
            color: $global-black;
        }

        &:disabled {
            opacity: 0.4;
            cursor: default;
        }
    }

    // --- Unified input pill ---
    &__input-shell {
        display: flex;
        align-items: center;
        gap: 4px;
        border: 1.5px solid $global-light-gray;
        border-radius: 10px;
        padding: 2px 4px;
        background: $global-white;
        transition: border-color 0.18s ease, box-shadow 0.18s ease;

        &:focus-within {
            border-color: $global-dark-3a;
            box-shadow: 0 0 0 3px rgba(58, 65, 70, 0.08);
        }
    }

    // --- Textarea area: overrides vMessageArea's own box ---
    &__area {
        flex: 1 1 auto;
        min-width: 0;
        align-self: stretch;
    }

    // --- Right-side actions group ---
    &__actions {
        display: flex;
        align-items: center;
        gap: 2px;
        flex: 0 0 auto;
    }

    // --- Base icon button ---
    &__btn {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: none;
        color: $global-gray;
        margin-bottom: 0;
        transition: background 0.15s ease, color 0.15s ease;

        &:hover:not(:disabled) {
            background: rgba(0, 0, 0, 0.06);
            color: $global-dark-3a;
        }

        &:disabled {
            color: $global-light-gray-blue;
            cursor: default;
        }

        &.is-active {
            color: $global-dark-3a;
            background: rgba(108, 79, 200, 0.1);
        }
    }

    // Emoji button — left side
    &__btn_emoji {
        margin-left: 2px;
        flex-shrink: 0;
    }

    // --- Send button: primary filled action ---
    // !important потрібен: button_type_icon / button_type_static мають таку ж специфічність
    // і перебивають background/color коли кнопка активна.
    &__btn_send {
        width: 32px;
        height: 32px;
        background: $global-dark-3a !important;
        color: $global-white !important;
        transition: background 0.15s ease, transform 0.12s ease;

        .preloader_type_button {
            margin-left: 0;
        }

        &:hover:not(:disabled) {
            background: darken($global-dark-3a, 8%) !important;
            color: $global-white !important;
            transform: scale(1.06);
        }

        &:disabled {
            background: $global-light-gray !important;
            color: $global-light-gray-blue !important;
            cursor: default;
            transform: none;
        }
    }

    &__emoji-icon {
        font-size: 18px;
        line-height: 1;
        display: block;
    }
}
.nc-composer__input-shell .message-area {
  border: none;
  padding: 0 4px;

}
</style>